import React, { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../services/firebase';
import './LoginPrompt.css';

interface LoginPromptProps {
  show: boolean;
  onClose: () => void;
  reason?: string;
  guestScore?: number;
  gamesPlayed?: number;
}

const LoginPrompt: React.FC<LoginPromptProps> = ({ show, onClose, reason, guestScore = 0, gamesPlayed = 0 }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!show) return null;

  const getPromptMessage = () => {
    switch (reason) {
      case 'first_game':
        return {
          title: '🎉 Great first game!',
          message: 'Sign in to save your progress and compete on the leaderboard'
        };
      case 'milestone_500':
        return {
          title: '🏆 You hit 500 points!',
          message: 'Amazing progress! Save your score to the leaderboard'
        };
      case 'milestone_1000':
        return {
          title: '🌟 1000 points! You\'re a star!',
          message: 'Don\'t lose this incredible achievement - save it now!'
        };
      case 'great_game':
        return {
          title: '🔥 That was exceptional!',
          message: 'Your skills deserve recognition on the leaderboard'
        };
      case 'games_milestone':
        return {
          title: `🎮 ${gamesPlayed} games played!`,
          message: 'You\'re on a roll! Save your progress to keep going'
        };
      default:
        return {
          title: '💫 Save Your Progress',
          message: 'Sign in to save your score and compete globally'
        };
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        // Transfer guest session scores
        // This will be handled by the parent component
        console.log('User signed in:', result.user.email);
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const { title, message } = getPromptMessage();

  return (
    <div className="login-prompt-overlay">
      <div className="login-prompt-modal">
        <button className="close-button" onClick={onClose}>×</button>

        <div className="prompt-content">
          <h2>{title}</h2>
          <p className="prompt-message">{message}</p>

          {guestScore > 0 && (
            <div className="score-display">
              <div className="score-item">
                <span className="score-label">Your Score</span>
                <span className="score-value">{guestScore}</span>
              </div>
              {gamesPlayed > 0 && (
                <div className="score-item">
                  <span className="score-label">Games Played</span>
                  <span className="score-value">{gamesPlayed}</span>
                </div>
              )}
            </div>
          )}

          <div className="benefits">
            <div className="benefit-item">✅ Save all your progress</div>
            <div className="benefit-item">✅ Compete on the leaderboard</div>
            <div className="benefit-item">✅ Track improvement over time</div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="action-buttons">
            <button
              className="google-signin-btn"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              {isLoading ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <svg className="google-icon" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </button>

            <button
              className="continue-guest-btn"
              onClick={onClose}
            >
              Continue as Guest
            </button>
          </div>

          <p className="privacy-note">
            Your progress will be saved securely. We never share your data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPrompt;